<template>
  <div class="page-wrap">
    <div class="page-inner-wrap">
      <close-btn :moduleName="moduleName"></close-btn>
      <page-title :moduleName="moduleName" :title="moduleTitle"></page-title>
      <article-banner
        class="banner"
        :banner="getIco('listbanner')"
        v-if="moduleConfig.BANNER"
      ></article-banner>
      <class-swiper
        :classList="classList"
        :activeClassId="activeClassId"
        @selectClass="handleSelectClass"
        v-if="moduleConfig.SHOW_CLASS"
      ></class-swiper>
      <search-jump
        :showTag="moduleConfig.SHOW_TAG"
        :source="pageSourceType.articleList"
        :moduleName="moduleName"
        v-if="moduleConfig.SHOW_SEARCH"
      ></search-jump>
      <article-lists
        class="article-lists"
        v-if="articleListConfig.configArr.length > 0"
        :articleList="
          articleListConfig.configArr[articleListConfig.activeClassIndex].list
        "
        :showLikes="moduleConfig.SHOW_LIKES"
        :articleType="moduleConfig.LIST_TYPE"
        @loadMore="loadMoreArticle(articleListConfig.activeClassIndex)"
        :hasMore="
          articleListConfig.configArr[articleListConfig.activeClassIndex]
            .nowPage <
          articleListConfig.configArr[articleListConfig.activeClassIndex]
            .totalPage
        "
        :source="pageSourceType.articleList"
        :moduleName="moduleName"
      ></article-lists>
    </div>
    <nav-bar></nav-bar>
  </div>
</template>
<script setup lang="ts">
import { reactive, ref, computed, watch } from "vue";
import PageTitle from "@components/common/PageTitle.vue";
import CloseBtn from "@components/common/CloseBtn.vue";
import ArticleBanner from "@components/article/ArticleBanner.vue";
import ClassSwiper from "@components/class/ClassSwiper.vue";
import ArticleLists from "@components/article/ArticleLists.vue";
import NavBar from "@components/common/NavBar.vue";
import SearchJump from "@components/common/SearchJump.vue";
import { getIco } from "@config/siteConfig";
import { useRoute } from "vue-router";
import { useBaseStore } from "@store/base";
import { useArticleStore } from "@store/article";
import { getWebsiteClass } from "@api/base";
import useModule from "@hooks/useModule";
import useLoading from "@hooks/useLoading";
import { getArticleList } from "@api/article";
import usePointViewPage from "@hooks/usePointViewPage";
const { setLoadingState } = useLoading();
const { moduleName, moduleConfig, moduleTitle } = useModule();
const { sendViewPagePoint } = usePointViewPage({
  pageKey: pageType.articleListPage,
  moduleName,
  isAutoSendViewPage: false,
});
const route = useRoute();
const base = useBaseStore();
const article = useArticleStore();

setLoadingState(true);

const classList = ref([]);
const activeClassId = ref(null);
const articleListConfig = reactive({
  activeClassIndex: 0,
  configArr: [],
});
const urlClassId = computed(() => {
  return route.query.classId;
});

const createArticleListItem = (classId) => {
  return {
    classId: classId,
    hasQuery: false,
    nowPage: 0,
    totalPage: 1,
    list: [],
  };
};

const initArticleConfig = () => {
  if (classList.value) {
    classList.value.forEach((item) => {
      articleListConfig.configArr.push(createArticleListItem(item.ID));
    });
  }
};

const getIndexByClassId = (classId) => {
  return classList.value.findIndex((item) => item.ID == classId);
};

const getItemByClassId = (classId) => {
  return classList.value.find((item) => item.ID == classId);
};

const handleSelectClass = async (classItem, source) => {
  setLoadingState(true);

  const classId = classItem.ID;
  const activeIndex = getIndexByClassId(classId);
  if (activeIndex != -1) {
    sendViewPagePoint(classId, source || pageSourceType.articleList);
    activeClassId.value = classId;
    articleListConfig.activeClassIndex = activeIndex;
    const nowArticleConfig = articleListConfig.configArr[activeIndex];
    if (nowArticleConfig.hasQuery) {
      setLoadingState(false);
      return;
    }
    // 请求文章列表
    const res = await getArticleList({
      classId: classId,
      module: moduleName,
      name: base.name,
      page: nowArticleConfig.nowPage + 1,
      language: base.lang,
      pageSize: article.articlePageSize,
      preview: base.preview,
      sign: base.sign,
      isDetail: 0,
    });
    if (res?.data) {
      nowArticleConfig.list = res?.data?.articlesInfo || [];
      nowArticleConfig.totalPage = res?.data?.totalPage || 1;
      nowArticleConfig.hasQuery = true;
      nowArticleConfig.nowPage = nowArticleConfig.nowPage + 1;
      setLoadingState(false);
    }
  }
};

const queryClassIdSelect = async (
  classId?: number | string,
  source?: string
) => {
  const cId = classId || urlClassId.value || "";
  if (cId) {
    const item = getItemByClassId(classId);
    if (item) {
      await handleSelectClass(item, source);
    }
  }
};

watch(
  urlClassId,
  (newId, oldId) => {
    if (!newId && oldId) {
      return;
    }
    if (newId) {
      queryClassIdSelect(newId, route.query.source);
    }
  },
  {
    immediate: true,
  }
);

const loadMoreArticle = async (activeClassIndex) => {
  setLoadingState(true);
  const nowArticleConfig = articleListConfig.configArr[activeClassIndex];
  // 请求文章列表
  const res = await getArticleList({
    classId: nowArticleConfig.classId,
    module: moduleName,
    name: base.name,
    language: base.lang,
    page: nowArticleConfig.nowPage + 1,
    pageSize: article.articlePageSize,
    isDetail: 0,
  });
  if (res?.data) {
    nowArticleConfig.list.push(...(res?.data?.articlesInfo || []));
    nowArticleConfig.totalPage = res?.data?.totalPage || 1;
  }
  nowArticleConfig.nowPage = nowArticleConfig.nowPage + 1;
  setLoadingState(false);
};

const initPageData = async () => {
  getWebsiteClass({
    name: base.name,
    module: moduleName,
    preview: base.preview,
    sign: base.sign,
    language: base.lang,
  })
    .then(async (res) => {
      if (res?.result && res?.data) {
        if (
          res.data?.classConfig &&
          Array.isArray(res.data.classConfig) &&
          res.data.classConfig.length > 0
        ) {
          classList.value = res.data.classConfig.map((item) => {
            let text = "";
            try {
              const textObj = JSON.parse(item.CLASS_NAME_MULTILINGUAL || "{}");
              text = textObj[base.lang] || textObj[base.defaultLang];
            } catch (_err) {
              text = "";
            }
            return {
              ...item,
              CLASS_NAME_MULTILINGUAL: text,
            };
          });
        } else {
          classList.value = [];
        }
        initArticleConfig();
        if (urlClassId.value) {
          await queryClassIdSelect(urlClassId.value, route.query.source);
        } else {
          await queryClassIdSelect(
            classList.value?.[0]?.ID || "",
            route.query.source
          );
        }
        setLoadingState(false);
      }
    })
    .catch((_err) => {
      console.log(_err);
    });
};
initPageData();
</script>

<style lang="scss" scoped>
@import "@assets/scss/dimen.scss";
@media (orientation: landscape) {
  .banner {
    display: none;
  }
}
</style>
